import React from 'react'
import { useI18n } from '../../../i18n'
import { Button } from '../../ui/Button'
import type { LinkedEvidenceRow } from '../../../services/questionEvidenceBindingApi'
import listStyles from '@/styles/pages/governance/GovernanceListing.module.scss'

function dedupKey(item: LinkedEvidenceRow): string {
  const answerId = String(item.questionAnswerId ?? '').trim()
  const ref = String(item.referenceType || 'EVIDENCE_ITEM').toUpperCase()
  const evidenceId = String(item.evidenceId ?? '').trim()
  const policyId = String(item.policyId ?? '').trim()
  const policyVersionId = String(item.policyVersionId ?? '').trim()
  const documentUuid = String(item.documentUuid ?? '').trim()
  const displayId = String(item.displayId ?? '').trim()
  return [answerId, ref, evidenceId, policyId, policyVersionId, documentUuid, displayId].join('|')
}

function dedupeLinkedItems(items: LinkedEvidenceRow[]): LinkedEvidenceRow[] {
  const seen = new Set<string>()
  const out: LinkedEvidenceRow[] = []
  for (const item of items) {
    const key = dedupKey(item)
    if (seen.has(key)) continue
    seen.add(key)
    out.push(item)
  }
  return out
}

interface Props {
  items: LinkedEvidenceRow[]
  onUnlink?: (item: LinkedEvidenceRow) => void
  unlinking?: boolean
}

function freshnessClass(status?: string) {
  const s = String(status || '').toLowerCase()
  if (s === 'fresh') return 'text-emerald-700'
  if (s === 'stale' || s === 'expired') return 'text-orange-700'
  if (s === 'aging') return 'text-amber-700'
  return listStyles.pickerRowMeta
}

export function LinkedEvidenceChips({ items, onUnlink, unlinking }: Props) {
  const { t } = useI18n()
  const rows = dedupeLinkedItems(items)
  if (!rows.length) {
    return (
      <p className={listStyles.pickerEmpty}>
        {t('complianceEvidence.noLinked', 'No evidence linked yet.')}
      </p>
    )
  }

  return (
    <ul className={listStyles.pickerList}>
      {rows.map((item) => (
        <li
          key={dedupKey(item)}
          className={listStyles.pickerRow}
        >
          <div className="min-w-0 flex-1">
            <p className={listStyles.pickerRowTitle}>
              {item.title || item.displayId || item.evidenceId || item.documentUuid}
            </p>
            <div className={`${listStyles.pickerRowMeta} mt-0.5 flex flex-wrap gap-x-2 gap-y-0.5`}>
              {item.source ? (
                <span className="font-medium">
                  {item.source === 'POLICY_LIBRARY'
                    ? t('governance.documentLifecycle.policyLibrary', 'Policy Library')
                    : item.source === 'PLATFORM_CONTROL'
                      ? t('complianceEvidence.platformSourceLabel', 'Platform evidence')
                      : t('governance.documentLifecycle.evidenceHub', 'Evidence Hub')}
                </span>
              ) : null}
              {item.displayId ? (
                <span className={listStyles.pickerRowMono}>{item.displayId}</span>
              ) : null}
              {item.version ? <span>v{item.version}</span> : null}
              {item.verificationStatus ? (
                <span>
                  {t('complianceEvidence.verified', 'Verified')}: {item.verificationStatus}
                </span>
              ) : null}
              {item.freshnessStatus ? (
                <span className={freshnessClass(item.freshnessStatus)}>
                  {t('complianceEvidence.freshness', 'Freshness')}: {item.freshnessStatus}
                </span>
              ) : null}
              {item.controlMappings?.length ? (
                <span>
                  {t('complianceEvidence.controls', 'Controls')}:{' '}
                  {item.controlMappings.map((c) => c.controlKey).join(', ')}
                </span>
              ) : null}
            </div>
          </div>
          {onUnlink ? (
            <Button
              type="button"
              size="small"
              color="danger"
              className="shrink-0"
              disabled={unlinking || !item.evidenceId}
              onClick={() => onUnlink(item)}
            >
              {t('complianceEvidence.unlink', 'Unlink')}
            </Button>
          ) : null}
        </li>
      ))}
    </ul>
  )
}
